import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  ref,
  set,
  update,
  remove,
  onValue,
  get,
  DatabaseReference
} from 'firebase/database';
import { database } from '../gen2ai/fire-init';
import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  User,
  AuthError
} from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';

export interface Note {
  id: string;
  userId: string;
  title: string;
  content: string;
  pinned: boolean;
  archived: boolean;
  labels: string[];
  color: string;
  order: number;     // for drag/drop ordering
  createdAt: number;
  updatedAt: number;
}

@Injectable({ providedIn: 'root' })
export class NotesService {
  private auth = getAuth();
  private notesRef: DatabaseReference | null = null;

  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();

  private notesSubject = new BehaviorSubject<Note[]>([]);
  notes$ = this.notesSubject.asObservable();

  private initialized = false;

  constructor() {
    this.initAuthListener();
  }

  /* ------------------------------------------------------------------ */
  /*  Auth                                                              */
  /* ------------------------------------------------------------------ */

  private initAuthListener(): void {
    if (this.initialized) return;
    this.initialized = true;

    onAuthStateChanged(this.auth, async (user) => {
      if (!user) {
        // fall back to anonymous auth
        const cred = await signInAnonymously(this.auth);
        user = cred.user;
      }
      this.userSubject.next(user);
      this.attachNotesListener(user.uid);
    });
  }

  async signInWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    try {
      await signInWithPopup(this.auth, provider);
    } catch (error) {
      const authError = error as AuthError;
      const popupRecoverableCodes = new Set([
        'auth/popup-blocked',
        'auth/popup-closed-by-user',
        'auth/cancelled-popup-request',
        'auth/operation-not-supported-in-this-environment'
      ]);

      if (popupRecoverableCodes.has(authError.code)) {
        await signInWithRedirect(this.auth, provider);
      }

      throw error;
    }
    // onAuthStateChanged will take care of notes listener
  }

  async signOut(): Promise<void> {
    await this.auth.signOut();
    this.userSubject.next(null);
    this.notesSubject.next([]);
    this.notesRef = null;
  }

  /* ------------------------------------------------------------------ */
  /*  Notes realtime sync                                               */
  /* ------------------------------------------------------------------ */

  private attachNotesListener(uid: string): void {
    this.notesRef = ref(database, `notes/${uid}`);
    onValue(this.notesRef, (snapshot) => {
      const notes: Note[] = [];
      const data = snapshot.val();
      if (data) {
        Object.keys(data).forEach((id) => {
          notes.push(data[id] as Note);
        });
      }
      // Sort pinned first by order, then others
      notes.sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return a.order - b.order;
      });
      this.notesSubject.next(notes);
    });
  }

  private ensureUserId(): string {
    const user = this.auth.currentUser;
    if (!user) throw new Error('User not initialized yet');
    return user.uid;
  }

  /* ------------------------------------------------------------------ */
  /*  CRUD                                                              */
  /* ------------------------------------------------------------------ */

  async createNote(partial: Partial<Note>): Promise<void> {
    const uid = this.ensureUserId();
    const id = uuidv4();
    const now = Date.now();

    const note: Note = {
      id,
      userId: uid,
      title: partial.title || '',
      content: partial.content || '',
      pinned: !!partial.pinned,
      archived: !!partial.archived,
      labels: partial.labels || [],
      color: partial.color || '#ffffff',
      order: partial.order ?? now,
      createdAt: now,
      updatedAt: now
    };

    await set(ref(database, `notes/${uid}/${id}`), note);
  }

  async updateNote(id: string, changes: Partial<Note>): Promise<void> {
    const uid = this.ensureUserId();
    const now = Date.now();
    await update(ref(database, `notes/${uid}/${id}`), {
      ...changes,
      updatedAt: now
    });
  }

  async deleteNote(id: string): Promise<void> {
    const uid = this.ensureUserId();
    await remove(ref(database, `notes/${uid}/${id}`));
  }

  async togglePinned(id: string, pinned: boolean): Promise<void> {
    await this.updateNote(id, { pinned });
  }

  async toggleArchived(id: string, archived: boolean): Promise<void> {
    await this.updateNote(id, { archived });
  }

  async shareNoteWithUser(note: Note, recipientEmail: string, message?: string): Promise<void> {
    const email = recipientEmail.trim().toLowerCase();
    if (!email) {
      throw new Error('Recipient email is required');
    }

    const usersSnapshot = await get(ref(database, 'users'));
    if (!usersSnapshot.exists()) {
      throw new Error('No users found');
    }

    const users = usersSnapshot.val() as Record<string, { email?: string | null }>;
    const recipientEntry = Object.entries(users).find(([, value]) => {
      const candidate = (value?.email || '').toLowerCase();
      return candidate === email;
    });

    if (!recipientEntry) {
      throw new Error('Recipient not found');
    }

    const [recipientUid] = recipientEntry;
    const now = Date.now();
    const id = uuidv4();
    const sharedContent = message?.trim()
      ? `${note.content || ''}\n\nShared message:\n${message.trim()}`.trim()
      : note.content || '';

    const sharedNote: Note = {
      id,
      userId: recipientUid,
      title: note.title || '',
      content: sharedContent,
      pinned: false,
      archived: false,
      labels: note.labels || [],
      color: note.color || '#ffffff',
      order: now,
      createdAt: now,
      updatedAt: now
    };

    await set(ref(database, `notes/${recipientUid}/${id}`), sharedNote);
  }

  /**
   * Persist new order for a list of notes (pinned or unpinned).
   * orderKeys is a list of note IDs in visual order.
   */
  async reorderNotes(orderKeys: string[]): Promise<void> {
    const uid = this.ensureUserId();
    const updates: any = {};
    orderKeys.forEach((id, idx) => {
      updates[`notes/${uid}/${id}/order`] = idx + 1;
    });
    await update(ref(database), updates);
  }
}
